import { Colors } from "../../../utils/colors";
import { useCallback, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getAccountState,
  reorderMembershipPlans,
  updateMembershipTheme,
} from "../../../redux/slices/accountSlice";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import { Icon } from "@iconify/react";
import { TabPanel } from "../../FullEventSubComponents/TabPanel";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import SwitchToggleItem from "../../SwitchToggleItem";
import { CircularProgress } from "@mui/material";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import {
  PlanTypeV4,
  MembershipPlan,
  MembershipState,
  NotificationType,
  OnboardingFlowType,
  StripeAccountStatus,
  ThemeType,
} from "@markit/common.types";
import { API } from "../../../API";
import ConfirmActionModal from "../../Containers/ConfirmPopups/ConfirmActionModal";
import { testStripe } from "../../../utils/stripeAccountUtils";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import {
  disableMemberships,
  getNumPaidMembershipSubscriptions,
} from "../../../utils/subscriptionUtils";
import AlertContainer from "../../Containers/AlertContainer";
import { useOnMount } from "../../../hooks/useOnMount";
import MembershipPlanItem from "./MembershipPlanItem";
import MembershipPlanModal from "./MembershipPlanModal";
import {
  hasSubscription,
  makeEmptyMembershipPlan,
  MARKIT_DOCS,
} from "@markit/common.utils";
import TextHoverButton from "../../Buttons/TextHoverButton";
import RectangleButton from "../../Buttons/RectangleButton";
import DragDropList from "../../Lists/DragDropList";
import ThemeDropdownToggle from "../Products/Components/ThemeDropdownToggle";
import { NavigationId } from "../../../navigation/AppParamList";
import { useNavigate } from "../../../hooks/useNavigate";
import DragDropListIcon from "../../Lists/DragDropListIcon";
import EmptyStateButtonSubscription from "../../Buttons/EmptyState/EmptyStateButtonSubscription";
import OnboardingTourStep from "../../Onboarding/OnboardingTourStep";
import OnboardingFlowDot from "../../Onboarding/OnboardingFlowDot";
import EnablePaidTicketsModal from "../../DisplayModal/EnablePaidTicketsModal";

export type MembershipPlansProps = {
  tabValue: number;
  setTabValue: (tabValue: number) => void;
  setListValue: (listValue: number) => void;
};

const MembershipPlans = (props: MembershipPlansProps) => {
  const { tabValue, setTabValue, setListValue } = props;
  const { accountData, readyForPayment } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [enablePaidMemberships, setEnablePaidMemberships] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stripeVisible, setStripeVisible] = useState(false);
  const [confirmDisableMemberships, setConfirmDisableMemberships] =
    useState(false);
  const [alertText, setAlertText] = useState({
    header: "",
    subHeader: "",
  });
  const [currMembershipPlan, setCurrMembershipPlan] =
    useState<MembershipPlan>();
  const [hasNoStripeAtMount] = useState(() => !readyForPayment);
  // Frozen at mount, same reasoning as hasNoStripeAtMount - if paid
  // memberships are already enabled when this tab first loads, the "toggle
  // this on" step is skipped and the tour is renumbered around it.
  const [membershipsDisabledAtMount] = useState(
    () =>
      !(
        hasSubscription(accountData) &&
        accountData.membership.state === MembershipState.ACTIVE &&
        accountData.membership.membershipPlans.length > 0
      )
  );
  const paidMembershipTotalSteps =
    (hasNoStripeAtMount ? 1 : 0) + (membershipsDisabledAtMount ? 1 : 0) + 3;
  const toggleStepNumber = (hasNoStripeAtMount ? 1 : 0) + 1;
  const newPlanStepNumber =
    (hasNoStripeAtMount ? 1 : 0) + (membershipsDisabledAtMount ? 1 : 0) + 1;
  const [paidMembershipTourStep, setPaidMembershipTourStep] = useState(1);

  const styles = {
    sectionContainer: {
      paddingBlock: 14,
      backgroundColor: Colors.WHITE,
    },
  };

  useOnMount(() => {
    if (accountData.membership.state === MembershipState.ACTIVE) {
      setEnablePaidMemberships(true);
    }
  });

  const hasSub = useMemo(() => hasSubscription(accountData), [accountData]);

  const membershipPlansEnabled = useMemo(
    () =>
      hasSub &&
      accountData.membership.state === MembershipState.ACTIVE &&
      accountData.membership.membershipPlans.length > 0,
    [accountData.membership, hasSub]
  );

  const userStripeAccountId = useMemo(
    () =>
      testStripe(accountData.uid)
        ? accountData.testStripeAccountId
        : accountData.stripeAccountId,
    [
      accountData.uid,
      accountData.stripeAccountId,
      accountData.testStripeAccountId,
    ]
  );

  const userStripeAccountStatus = useMemo(
    () =>
      testStripe(accountData.uid)
        ? accountData.testStripeAccountStatus
        : accountData.stripeAccountStatus,
    [
      accountData.uid,
      accountData.stripeAccountStatus,
      accountData.testStripeAccountStatus,
    ]
  );

  const awaitingManualReview = useMemo(
    () => userStripeAccountStatus === StripeAccountStatus.MANUAL_REVIEW,
    [userStripeAccountStatus]
  );

  const stripeConnectMessage = useMemo(() => {
    if (userStripeAccountStatus === StripeAccountStatus.INCOMPLETE) {
      if (userStripeAccountId === "") {
        return "Connect Stripe to Enable Paid Plans";
      } else {
        return "Finish Connecting Stripe to Enable Paid Plans";
      }
    } else if (awaitingManualReview) {
      return "Awaiting Manual Review from our Team";
    } else {
      return "Manage Stripe Account";
    }
  }, [userStripeAccountId, userStripeAccountStatus, awaitingManualReview]);

  const checkNumPaidMemberships = useCallback(async () => {
    const numPaidSubs = await getNumPaidMembershipSubscriptions(
      accountData.uid
    );
    return numPaidSubs;
  }, [accountData.uid]);

  const toggleMemberships = useCallback(async () => {
    if (!enablePaidMemberships) {
      setLoading(true);
      setEnablePaidMemberships(true);
      showNotificationBanner(
        dispatch,
        "Enabled Memberships!",
        NotificationType.AFFIRMATIVE
      );
      await API.user.initializeMembershipPlans({ uid: accountData.uid });
      setLoading(false);
    } else {
      const numPaidSubscriptions = await checkNumPaidMemberships();
      if (numPaidSubscriptions > 0) {
        setAlertText({
          header: "You cannot turn off paid memberships.",
          subHeader:
            "You have one or more paid subscribers. Contact support if you need help.",
        });
      } else {
        setConfirmDisableMemberships(true);
      }
    }
  }, [
    accountData.uid,
    checkNumPaidMemberships,
    dispatch,
    enablePaidMemberships,
  ]);

  const toggleMembershipTheme = useCallback(
    (theme: ThemeType) => {
      dispatch(updateMembershipTheme(accountData.uid, theme));
    },
    [dispatch, accountData.uid]
  );

  const disableMembershipPlans = useCallback(async () => {
    await disableMemberships(accountData.uid, accountData.membership);
    showNotificationBanner(
      dispatch,
      "Disabled Memberships",
      NotificationType.AFFIRMATIVE
    );
    setEnablePaidMemberships(false);
    setConfirmDisableMemberships(false);
  }, [accountData.membership, accountData.uid, dispatch]);

  const viewMembershipPlanSubscribers = useCallback(
    (membershipId: string) => {
      setTabValue(0);
      setListValue(membershipId === "free" ? 1 : 0);
    },
    [setListValue, setTabValue]
  );

  const manageStripe = useCallback(async () => {
    if (readyForPayment) {
      navigate(NavigationId.HOME_PAYOUTS);
    } else {
      setStripeVisible(true);
    }
  }, [navigate, readyForPayment]);

  const handleOnDrag = useCallback(
    (newPlans: MembershipPlan[]) => {
      dispatch(reorderMembershipPlans(accountData.uid, newPlans));
    },
    [dispatch, accountData.uid]
  );

  return (
    <TabPanel value={tabValue} index={2}>
      <div
        className="ColumnNormal"
        style={{
          width: 544,
          marginInline: "auto",
          gap: 20,
        }}
      >
        <StandardBorderedContainer containerStyles={styles.sectionContainer}>
          <div className="ColumnNormal" style={{ gap: 4, paddingInline: 14 }}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <span className="sectionTitle">Paid Memberships</span>
              <Icon
                icon={"ion:ribbon"}
                height={18}
                style={{ color: Colors.BLACK }}
              />
            </div>
            <span className="smallBodySubtext">
              Make money from texting. Charge monthly and annual fees from
              subscribers.
            </span>
          </div>
          <HorizontalDivider altMargin={14} />
          <div
            className="onboarding-paid-membership-toggle-target"
            style={{ paddingInline: 14 }}
          >
            <SwitchToggleItem
              title="Enable Paid Memberships"
              description="Enable and connect to Stripe to enable paid memberships"
              toggleValue={enablePaidMemberships}
              onChange={toggleMemberships}
              disabled={!readyForPayment || !hasSub}
            />
          </div>
          <HorizontalDivider altMargin={14} />
          {membershipPlansEnabled ? (
            <>
              <div className="AlignedRowSpaced" style={{ paddingInline: 14 }}>
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <span className="bodyMedium">Membership Page Theme</span>
                  <span className="smallBodySubtext">
                    Customize your membership sign up page
                  </span>
                </div>
                <div
                  style={{
                    padding: 14,
                    border: `0.5px solid ${Colors.GRAY2}`,
                    borderRadius: 12,
                  }}
                >
                  <ThemeDropdownToggle
                    visualThemeType={ThemeType.Light}
                    themeType={accountData.membership.theme}
                    onPress={toggleMembershipTheme}
                  />
                </div>
              </div>
              <HorizontalDivider altMargin={14} />
            </>
          ) : null}
          <div
            className="AlignedRowSpacedSelect onboarding-paid-membership-stripe-target"
            style={{ paddingInline: 14 }}
            onClick={manageStripe}
          >
            <div className="AlignedRowSelect" style={{ gap: 14 }}>
              <Icon
                icon="fa6-brands:cc-stripe"
                height={25}
                style={{ color: Colors.PURPLE11 }}
              />
              <div className="ColumnNormalSelect" style={{ gap: 4 }}>
                <span className="bodyMedium">
                  {!readyForPayment
                    ? stripeConnectMessage
                    : "Manage Stripe Account"}
                </span>
                <div className="ColumnNormal" style={{ gap: 2 }}>
                  <span className="smallBodySubtext">
                    Markit charges zero fees on memberships, other than standard
                    Stripe processing fees.
                  </span>
                  <TextHoverButton
                    text="Learn more about fees"
                    onPress={(e) => {
                      e?.stopPropagation();
                      window.open(MARKIT_DOCS + "/fees");
                    }}
                    icon={
                      <Icon
                        icon="ion:chevron-forward"
                        height={12}
                        style={{ color: Colors.GRAY1, marginBottom: -2 }}
                      />
                    }
                    iconPosition="right"
                  />
                </div>
              </div>
            </div>
            <Icon
              icon="mdi:chevron-right"
              height={24}
              style={{ color: Colors.GRAY1 }}
            />
          </div>
        </StandardBorderedContainer>
        <div className="AlignedRow" style={{ gap: 7 }}>
          <span className="sectionTitle">Membership Plans</span>
          {!hasSubscription(accountData) ? (
            <OnboardingFlowDot
              flowTypes={[OnboardingFlowType.PAID_MEMBERSHIP]}
            />
          ) : null}
        </div>
        <div className="ColumnNormal">
          {loading ? (
            <div className="Centering" style={{ paddingBlock: 80 }}>
              <CircularProgress style={{ color: Colors.BLACK }} size={24} />
            </div>
          ) : membershipPlansEnabled ? (
            <div className="ColumnNormal" style={{ paddingBottom: 80 }}>
              <div
                className="ColumnNormal onboarding-paid-membership-newplan-target"
                style={{ gap: 10 }}
              >
                <DragDropList
                  items={accountData.membership.membershipPlans}
                  handleChange={handleOnDrag}
                >
                  {(item, _, dragHandleProps: any) => (
                    <div className="RowNormal">
                      <MembershipPlanItem
                        key={item.id}
                        membershipPlan={item}
                        viewPlanSubscribers={viewMembershipPlanSubscribers}
                        setCurrMembershipPlan={setCurrMembershipPlan}
                        dragIcon={
                          <DragDropListIcon dragHandleProps={dragHandleProps} />
                        }
                      />
                    </div>
                  )}
                </DragDropList>
                <div style={{ margin: "0 auto" }}>
                  <RectangleButton
                    buttonLabel="New Plan"
                    onPress={() => {
                      const nonFreeMembershipPlans =
                        accountData.membership.membershipPlans.filter(
                          (plan) => plan.id !== "free"
                        );
                      const membershipPlan = makeEmptyMembershipPlan(
                        nonFreeMembershipPlans.length + 1
                      );
                      setCurrMembershipPlan(membershipPlan);
                    }}
                    altTextColor={Colors.BLACK}
                    containerStyles={{
                      whiteSpace: "nowrap",
                      padding: 14,
                      backgroundColor: Colors.WHITE,
                      border: `0.5px solid ${Colors.GRAY2}`,
                      width: "fit-content",
                    }}
                    iconRight={
                      <Icon
                        icon={"ion:plus"}
                        height={14}
                        style={{ color: Colors.BLACK }}
                      />
                    }
                  />
                </div>
              </div>
            </div>
          ) : !hasSub ? (
            <EmptyStateButtonSubscription
              planType={PlanTypeV4.ESSENTIAL}
              iconName={"ion:ribbon"}
              description="Start earning by creating paid subscriber plans"
              btnText="Get Essential Plan"
              containerStyles={{ paddingBlock: 40 }}
            />
          ) : (
            <EmptyStateButton
              title={
                !readyForPayment
                  ? stripeConnectMessage
                  : "Enable Paid Plans to Start Earning"
              }
              description={
                awaitingManualReview
                  ? "Your new Markit Stripe account is being reviewed by our team. We will approve your account within 12 hours (usually much sooner)."
                  : "Your free and paid plans will show up here once you connect to Stripe and enable paid plans."
              }
              icon={
                <Icon
                  icon={readyForPayment ? "ion:ribbon" : "fa6-brands:cc-stripe"}
                  height={50}
                  style={{
                    color: readyForPayment ? Colors.GREEN2 : Colors.PURPLE11,
                  }}
                />
              }
              iconBox={80}
              iconBackgroundColor={Colors.WHITE}
              btnText={
                !readyForPayment
                  ? userStripeAccountStatus === StripeAccountStatus.INCOMPLETE
                    ? "Connect to Stripe"
                    : undefined
                  : "Enable Paid Plans"
              }
              onPress={!readyForPayment ? manageStripe : toggleMemberships}
              containerStyles={{ paddingBlock: 80 }}
              buttonContainerStyles={{
                backgroundColor: !readyForPayment
                  ? Colors.BLACK
                  : Colors.GREEN2,
              }}
            />
          )}
        </div>
      </div>
      {currMembershipPlan ? (
        <MembershipPlanModal
          currMembershipPlan={currMembershipPlan}
          setCurrMembershipPlan={setCurrMembershipPlan}
          tourStepNumber={newPlanStepNumber + 1}
          tourTotalSteps={paidMembershipTotalSteps}
        />
      ) : null}
      {stripeVisible ? (
        <EnablePaidTicketsModal
          isVisible={stripeVisible}
          setIsVisible={setStripeVisible}
        />
      ) : null}
      {hasSub && !currMembershipPlan ? (
        hasNoStripeAtMount && paidMembershipTourStep === 1 ? (
          <OnboardingTourStep
            key="paid-membership-tour-step-1"
            flowType={OnboardingFlowType.PAID_MEMBERSHIP}
            target=".onboarding-paid-membership-stripe-target"
            content={
              "In order to enable paid memberships, you must first connect your Stripe account."
            }
            stepNumber={1}
            totalSteps={paidMembershipTotalSteps}
            placement="right"
            onStepComplete={() => setPaidMembershipTourStep(2)}
          />
        ) : membershipsDisabledAtMount && !membershipPlansEnabled ? (
          <OnboardingTourStep
            key="paid-membership-tour-step-toggle"
            flowType={OnboardingFlowType.PAID_MEMBERSHIP}
            target=".onboarding-paid-membership-toggle-target"
            content={
              <>
                With paid memberships, you can create and charge monthly
                recurring plans to promote exlusive benefits for your
                subscribers.
                <br />
                <br />
                Toggle this on to enable paid memberships.
              </>
            }
            stepNumber={toggleStepNumber}
            totalSteps={paidMembershipTotalSteps}
            placement="right"
          />
        ) : membershipPlansEnabled ? (
          <OnboardingTourStep
            key="paid-membership-tour-step-newplan"
            flowType={OnboardingFlowType.PAID_MEMBERSHIP}
            target=".onboarding-paid-membership-newplan-target"
            content={
              <>
                Here are your membership plans. We&apos;ve created a couple to
                get you started. Edit these or press &quot;New Plan&quot; to
                start from scratch.
              </>
            }
            stepNumber={newPlanStepNumber}
            totalSteps={paidMembershipTotalSteps}
            mountDelayMs={300}
          />
        ) : null
      ) : null}
      <ConfirmActionModal
        heading={"Turn Off Paid Memberships?"}
        subtext={"Are you sure you want turn off paid memberships?"}
        confirmButtonText="Yes"
        hideModal={!confirmDisableMemberships}
        setIsVisible={setConfirmDisableMemberships}
        confirmOnPress={disableMembershipPlans}
      />
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
    </TabPanel>
  );
};

export default MembershipPlans;
